import React from 'react'
import { motion, useAnimation, useInView } from 'framer-motion'
import { SiSemanticscholar } from 'react-icons/si'
import { BiUserVoice } from 'react-icons/bi'
import { RiHandCoinLine } from 'react-icons/ri'
import { LiaUniversitySolid, LiaHandsHelpingSolid } from 'react-icons/lia'
import { MdOutlineAssignment } from 'react-icons/md'
import { TiTickOutline } from 'react-icons/ti'
import { TbProgressCheck } from 'react-icons/tb'
import { AiOutlineSolution } from 'react-icons/ai'

const features = [
  { icon: <TiTickOutline />, title: 'Expertise', desc: 'Experienced professionals guide you through global university admissions.', key: 'expertise' },
  { icon: <AiOutlineSolution />, title: 'Customized Solutions', desc: 'Tailored plans aligning with your unique profile and goals.', key: 'custom' },
  { icon: <TbProgressCheck />, title: 'Transparency', desc: 'Clear, timely updates at every step of your application journey.', key: 'transparency' },
  { icon: <RiHandCoinLine />, title: 'Affordable Services', desc: 'Premium guidance without premium fees—best value packages.', key: 'affordable' },
  { icon: <LiaUniversitySolid />, title: 'University Selection', desc: 'Data-driven matches to courses and institutions worldwide.', key: 'selection' },
  { icon: <LiaHandsHelpingSolid />, title: 'Application Assistance', desc: 'Hands-on help with SOPs, essays, and all application docs.', key: 'assistance' },
  { icon: <MdOutlineAssignment />, title: 'Visa Assistance', desc: 'Seamless visa prep, from document checklists to mock interviews.', key: 'visa' },
  { icon: <BiUserVoice />, title: 'Pre-Departure Briefing', desc: 'In-depth orientation on travel, housing, and campus life abroad.', key: 'briefing' },
  { icon: <SiSemanticscholar />, title: 'Scholarship Assistance', desc: 'Locate and apply for scholarships to ease your financial journey.', key: 'scholarship' }
]

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
      when: 'beforeChildren'
    }
  }
}

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
}

const WhyUs = () => {
  const controls = useAnimation()
  const ref = React.useRef(null)
  const inView = useInView(ref, { once: true, margin: '-100px' })

  React.useEffect(() => {
    if (inView) controls.start('visible')
  }, [controls, inView])

  return (
    <section className="bg-gray-50 py-16" aria-labelledby="whyus-heading">
      <div className="text-center mb-12">
        <h2 id="whyus-heading" className="text-3xl font-extrabold text-gray-900">Why Choose Globalpro?</h2>
        <p className="mt-2 text-gray-600">Nine distinct advantages crafted for your success abroad.</p>
      </div>

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={controls}
        className="max-w-5xl mx-auto grid gap-6 sm:grid-cols-2 lg:grid-cols-3 px-4"
      >
        {features.map(f => (
          <motion.div
            key={f.key}
            variants={cardVariants}
            whileHover={{ scale: 1.03 }}
            transition={{ type: 'spring', stiffness: 300 }}
            className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl focus-within:ring-2 focus-within:ring-indigo-300 transition-all cursor-pointer"
            tabIndex={0}
            role="group"
            aria-describedby={`${f.key}-desc`}
          >
            <div className="flex items-center justify-center mb-4 w-12 h-12 rounded-full bg-indigo-100 group-focus:bg-indigo-200 transition-colors">
              {React.cloneElement(f.icon, { className: 'w-6 h-6 text-indigo-600' })}
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1 group-focus:text-indigo-600">{f.title}</h3>
            <p id={`${f.key}-desc`} className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </section>
  )
}

export default WhyUs
